import type { Destination } from '../destination';
import { lazySdk, urlOf } from '../lazy-sdk';
import { toVendorEvent } from '../mapping';

// Plausible's documented opt-out, honoured by both the script
// index-template.ejs loads and the `plausible-tracker` client used here.
const PLAUSIBLE_IGNORE_KEY = 'plausible_ignore';

function setIgnored(ignored: boolean) {
	try {
		if (ignored) {
			localStorage.setItem(PLAUSIBLE_IGNORE_KEY, 'true');
		} else {
			localStorage.removeItem(PLAUSIBLE_IGNORE_KEY);
		}
	} catch {
		// Private mode etc.: nothing else to do.
	}
}

/**
 * Plausible: `screen_view` is a page view, every other event a custom event
 * with the row as its properties. Pass-through: no insights guarantee.
 */
export function createPlausibleDestination(domain: string): Destination {
	const plausible = lazySdk(() =>
		import('plausible-tracker').then((module) =>
			module.default({ domain, trackLocalhost: true }),
		),
	);

	return {
		name: 'plausible',
		start: () => {
			setIgnored(false);
			plausible.load();
		},
		stop: () => setIgnored(true),
		send: (event) => {
			const { name, params } = toVendorEvent(event);
			plausible.use((client) => {
				if (event.name === 'screen_view') {
					client.trackPageview({ url: urlOf(event.path) }, { props: params });
				} else {
					client.trackEvent(name, { props: params });
				}
			});
		},
	};
}
